import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useToastStore, type Toast } from './toastStore';

const toneStyles = {
  info: { bar: 'bg-violet-400', Icon: Info, iconColour: 'text-violet-400' },
  success: { bar: 'bg-mint-400', Icon: CheckCircle2, iconColour: 'text-mint-600' },
  warning: { bar: 'bg-warning-400', Icon: AlertTriangle, iconColour: 'text-warning-400' },
  danger: { bar: 'bg-danger-400', Icon: AlertCircle, iconColour: 'text-danger-400' },
} as const;

const ToastCard = ({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) => {
  const style = toneStyles[toast.tone];
  const { Icon } = style;
  return (
    <div role="alert" className="bg-paper rounded-md shadow-pop pointer-events-auto overflow-hidden flex animate-slide-up">
      <div className={cn('w-1 shrink-0', style.bar)} aria-hidden />
      <div className="flex-1 p-3 flex gap-3">
        <Icon size={18} className={cn('shrink-0 mt-0.5', style.iconColour)} />
        <div className="flex-1 min-w-0">
          <p className="text-small font-medium text-ink-900">{toast.title}</p>
          {toast.description && (
            <p className="text-xs text-ink-500 mt-0.5">{toast.description}</p>
          )}
          {toast.action && (
            <button
              type="button"
              onClick={toast.action.onClick}
              className="text-xs font-medium text-violet-600 hover:text-violet-800 mt-1.5"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-ink-300 hover:text-ink-500 transition-colors"
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export const Toaster = () => {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  if (typeof window === 'undefined') return null;

  return createPortal(
    <div
      role="region"
      aria-label="Notifications"
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>,
    document.body,
  );
};
